import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // PHASE 1.5 VERIFIER: r2-pa-import-8c4d2e91
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config';
import { Loader2, MapPin, Calendar } from 'lucide-react';
import { getJobArrivalWindowLabel } from '../utils/arrivalWindows'; // portalwin-3e7a2c95

// portalwin-3e7a2c95 — what this card should say about WHEN.
//
// The portal used to print the literal words "Arrival window:" followed by
// startAt's clock time, and never read arrivalWindow at all. Because a windowed
// job's startAt is collapsed onto the window's START hour
// (JobFormModal.js:2155-2164), a customer promised "Morning (8am – 12pm)" was
// shown "8:00 AM" — a precision nobody had committed to, on ~95% of jobs, and
// contradicting the confirmation email they had already received
// (functions/index.js:9695-9697).
//
// Uses getJobArrivalWindowLabel, the shared resolver, which reads the job's
// arrivalWindowSnapshot first and live-resolves its key otherwise. Snapshot-first
// is what lets this respect per-org window labels WITHOUT the portal fetching the
// org doc — the snapshot exists precisely so render sites need no companySettings
// (arrivalWindows.js:155-176), and it is the same call shape RoutesView.js:564 and
// JobManagementView.js:248 already use.
//
// Three cases, because "show the time" is only right for one of them:
//   window  -> the window label, matching the email
//   all-day -> "All day"; its startAt is local midnight, so a time would read
//              "12:00 AM", which is a third way of being wrong
//   exact   -> the clock time, which for these IS the commitment. Relabelled
//              "Scheduled time" because an exact appointment is not a window.
const resolveScheduleLine = (job, timeStr) => {
    const windowLabel = getJobArrivalWindowLabel(job);
    if (job?.schedulingMode === 'window' && windowLabel) {
        return { label: 'Arrival window', value: windowLabel };
    }
    if (job?.isAllDay === true) {
        return { label: 'Scheduled', value: 'All day' };
    }
    return { label: 'Scheduled time', value: timeStr };
};

const PortalAppointments = () => {
    const [appointments, setAppointments] = useState({ upcoming: [], past: [] });
    const [loading, setLoading] = useState(true);
    // SEAM 1 VERIFIER: portalevt-6c2f9a13
    // Distinguishes "we could not load your appointments" from "you have none".
    // See the catch block and the loadError render branch below.
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        const fetchAppointments = async () => {
            const user = auth.currentUser;
            // SEAM 1 VERIFIER: portalevt-6c2f9a13
            // Was `if (!user) return;`, which left loading===true forever and
            // spun the loader indefinitely. Same family as the swallowed-error
            // bug below: a failure that renders as something other than a
            // failure.
            if (!user) { setLoading(false); return; }

            try {
                // s6portal-7b3e9d52 — S6a (security audit #2). The portal no longer
                // reads job documents: a whole job carried every worker's labour
                // cost rate (jobCosting) to the customer's browser. The callable
                // takes org + customer from the login token, applies the
                // customer-facing event-type boundary (Job / Appointment only) on
                // the server, and returns ONLY the fields this page shows — see
                // functions/utils/portalAppointments.js. startAt arrives as an ISO
                // string (newest first, the order the old query used).
                const getAppointments = httpsCallable(functions, 'getCustomerAppointments');
                const result = await getAppointments({});
                const list = Array.isArray(result.data?.appointments) ? result.data.appointments : [];
                const now = new Date();

                const upcoming = [];
                const past = [];

                list.forEach(job => {
                    const jobDate = job.startAt ? new Date(job.startAt) : null;
                    if (jobDate && jobDate >= now) {
                        upcoming.push(job);
                    } else {
                        past.push(job);
                    }
                });

                upcoming.sort((a, b) => new Date(a.startAt) - new Date(b.startAt));

                setAppointments({ upcoming, past });

            } catch (error) {
                // SEAM 1 VERIFIER: portalevt-6c2f9a13
                // A failed fetch must never render as an empty calendar. Before
                // this, ANY error -- a not-yet-built composite index, a rules
                // denial from a stale bundle after the rules tighten, an expired
                // token, a network failure -- fell through to the empty state and
                // the customer read "No upcoming visits scheduled." as "you have
                // nothing booked". The empty-state copy below is unchanged, so it
                // now means exactly one thing.
                console.error("Error fetching appointments:", error);
                setLoadError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchAppointments();
    }, []);

    const AppointmentCard = ({ job }) => {
        const dateObj = job.startAt ? new Date(job.startAt) : new Date(); // s6portal-7b3e9d52 — ISO string from the callable
        const dateStr = dateObj.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
        const timeStr = dateObj.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
        const schedule = resolveScheduleLine(job, timeStr); // portalwin-3e7a2c95

        return (
            <Link to={`/portal/appointments/${job.id}`} className="block hover:shadow-md transition-shadow"> {/* PHASE 1.5 VERIFIER: r2-pa-link-8c4d2e91 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
                <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                        <Calendar size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{dateStr}</h3>
                        {/* portalwin-3e7a2c95 */}
                        {schedule.value && (
                            <p className="text-gray-500 text-sm mb-2">{schedule.label}: {schedule.value}</p>
                        )}
                        
                        {/* Address Section */}
                        {(job.location || job.address) && (
                            <div className="flex items-center gap-2 text-gray-600 mt-3 bg-gray-50 p-2 rounded-md inline-block">
                                <MapPin size={16} />
                                <span className="text-sm font-medium">{job.location || job.address}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </Link>
        );
    };

    if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-green-600" /></div>;

    // SEAM 1 VERIFIER: portalevt-6c2f9a13
    // A broken portal must not look like an empty one. This branch is reached
    // only from the catch above; the empty-state copy further down is untouched.
    if (loadError) return (
        <div className="max-w-3xl">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Your Appointments</h1>
            <div className="bg-white p-10 rounded-lg shadow-sm border border-gray-200 text-center">
                <p className="text-gray-700 font-medium mb-2">We couldn't load your appointments.</p>
                <p className="text-gray-500 text-sm mb-4">
                    This is a problem on our end — it doesn't mean you have nothing scheduled.
                </p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
                >
                    Try again
                </button>
            </div>
        </div>
    );

    return (
        <div className="max-w-3xl">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Your Appointments</h1>

            {/* UPCOMING SECTION */}
            <div className="mb-10">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Upcoming</h2>
                {appointments.upcoming.length === 0 ? (
                    <p className="text-gray-500 italic bg-gray-50 p-4 rounded-md border border-gray-100">
                        No upcoming visits scheduled.
                    </p>
                ) : (
                    appointments.upcoming.map(job => <AppointmentCard key={job.id} job={job} />)
                )}
            </div>

            {/* PAST SECTION */}
            <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4">Past</h2>
                {appointments.past.length === 0 ? (
                    <p className="text-gray-500 italic">No past history found.</p>
                ) : (
                    appointments.past.map(job => <AppointmentCard key={job.id} job={job} />)
                )}
            </div>
        </div>
    );
};

export default PortalAppointments;